import React, { useState } from "react";
import { Table, Card, Text, Badge, Button, Spinner } from "@radix-ui/themes";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
} from "@radix-ui/react-icons";
import { LinkRecordFull } from "winslow-core";
import {
  apiGetLinkRecordContent,
  LinkRecordContentResponse,
} from "../../services/KnowledgeBaseService";
import "./KnowledgeBaseTable.css";

interface KnowledgeBaseTableProps {
  linkRecords: LinkRecordFull[];
}

interface ContentCache {
  [linkRecordId: string]: LinkRecordContentResponse;
}

export const KnowledgeBaseTable: React.FC<KnowledgeBaseTableProps> = ({
  linkRecords,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [contentCache, setContentCache] = useState<ContentCache>({});
  const [loadingContent, setLoadingContent] = useState<Set<string>>(new Set());

  const formatDate = (dateString: Date | string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const toggleRow = async (linkRecordId: string) => {
    const newExpandedRows = new Set(expandedRows);

    if (newExpandedRows.has(linkRecordId)) {
      newExpandedRows.delete(linkRecordId);
    } else {
      newExpandedRows.add(linkRecordId);
      if (!contentCache[linkRecordId]) {
        await loadContent(linkRecordId);
      }
    }

    setExpandedRows(newExpandedRows);
  };

  const loadContent = async (linkRecordId: string) => {
    setLoadingContent((prev) => new Set(prev).add(linkRecordId));

    try {
      const content = await apiGetLinkRecordContent(linkRecordId);
      if (content) {
        setContentCache((prev) => ({
          ...prev,
          [linkRecordId]: content,
        }));
      }
    } catch (error) {
      console.error("Error loading content:", error);
    } finally {
      setLoadingContent((prev) => {
        const newSet = new Set(prev);
        newSet.delete(linkRecordId);
        return newSet;
      });
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      console.log(`${label} copied to clipboard`);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  const getTitle = (linkRecord: LinkRecordFull) => {
    if (linkRecord.links && linkRecord.links.length > 0) {
      return linkRecord.links[0].title || linkRecord.links[0].url || "Untitled";
    }
    return "Untitled";
  };

  const getUrl = (linkRecord: LinkRecordFull) => {
    if (linkRecord.links && linkRecord.links.length > 0) {
      return linkRecord.links[0].url || "N/A";
    }
    return "N/A";
  };

  const getEmbeddedStatusColor = (status: string) => {
    switch (status) {
      case "PROCESSED":
        return "green";
      case "NOT_PROCESSED":
        return "yellow";
      case "TOO_LARGE":
      case "FORMAT_NOT_SUPPORTED":
      case "NO_CONTENT":
      case "SYSTEM_OUTAGE":
      case "UNKNOWN":
        return "red";
      default:
        return "gray";
    }
  };

  const truncateUrl = (url: string, maxLength: number = 50) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  return (
    <div className="knowledge-base-table">
      <Table.Root size="2" variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Toggle</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>URL</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Tokens</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {linkRecords.map((linkRecord) => (
            <React.Fragment key={linkRecord.id}>
              <Table.Row>
                <Table.Cell>
                  <button
                    onClick={() => toggleRow(linkRecord.id)}
                    className="row-toggle-button">
                    {expandedRows.has(linkRecord.id) ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </button>
                </Table.Cell>
                <Table.Cell>
                  <div className="link-record-title">{getTitle(linkRecord)}</div>
                </Table.Cell>
                <Table.Cell>
                  <div className="link-record-url" title={getUrl(linkRecord)}>
                    {truncateUrl(getUrl(linkRecord))}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    color={getEmbeddedStatusColor(linkRecord.embeddedStatus)}>
                    {linkRecord.embeddedStatus}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <div className="link-record-tokens">
                    {linkRecord.tokenCount.toLocaleString()}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <Badge color="blue">{linkRecord.source}</Badge>
                </Table.Cell>
                <Table.Cell>
                  <div className="link-record-date">
                    {formatDate(linkRecord.createdAt)}
                  </div>
                </Table.Cell>
              </Table.Row>
              {expandedRows.has(linkRecord.id) && (
                <Table.Row>
                  <Table.Cell colSpan={7}>
                    <Card className="link-record-details-card">
                      <div className="link-record-details-header">
                        <Text size="4" weight="bold">
                          Document Content
                        </Text>
                        {contentCache[linkRecord.id] && (
                          <div className="content-actions">
                            <Text size="2" color="gray">
                              {contentCache[linkRecord.id].totalChunks} chunk
                              {contentCache[linkRecord.id].totalChunks !== 1
                                ? "s"
                                : ""}
                            </Text>
                            <Button
                              size="1"
                              variant="ghost"
                              onClick={() =>
                                copyToClipboard(
                                  contentCache[linkRecord.id].content,
                                  "Content"
                                )
                              }>
                              <CopyIcon width="14" height="14" />
                              Copy
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="link-record-content-container">
                        {loadingContent.has(linkRecord.id) ? (
                          <div className="content-loading">
                            <Spinner size="2" />
                            <Text size="2">Loading content...</Text>
                          </div>
                        ) : contentCache[linkRecord.id] ? (
                          contentCache[linkRecord.id].content ? (
                            <pre className="link-record-content">
                              {contentCache[linkRecord.id].content}
                            </pre>
                          ) : (
                            <div className="content-empty">
                              <Text size="2" color="gray">
                                No content available for this document.
                              </Text>
                            </div>
                          )
                        ) : (
                          <div className="content-error">
                            <Text size="2" color="red">
                              Failed to load content.
                            </Text>
                            <Button
                              size="1"
                              variant="soft"
                              onClick={() => loadContent(linkRecord.id)}>
                              Retry
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="link-record-metadata">
                        <div className="metadata-row">
                          <Text weight="medium" size="2">
                            ID:
                          </Text>
                          <Text size="2" className="metadata-value">
                            {linkRecord.id}
                          </Text>
                        </div>
                        <div className="metadata-row">
                          <Text weight="medium" size="2">
                            Collection ID:
                          </Text>
                          <Text size="2" className="metadata-value">
                            {linkRecord.collectionId}
                          </Text>
                        </div>
                        <div className="metadata-row">
                          <Text weight="medium" size="2">
                            Canonical ID:
                          </Text>
                          <Text size="2" className="metadata-value">
                            {linkRecord.canonicalId}
                          </Text>
                        </div>
                        {linkRecord.mimeType && (
                          <div className="metadata-row">
                            <Text weight="medium" size="2">
                              MIME Type:
                            </Text>
                            <Text size="2" className="metadata-value">
                              {linkRecord.mimeType}
                            </Text>
                          </div>
                        )}
                      </div>
                    </Card>
                  </Table.Cell>
                </Table.Row>
              )}
            </React.Fragment>
          ))}
        </Table.Body>
      </Table.Root>
      {linkRecords.length === 0 && (
        <div className="knowledge-base-table-empty">
          No documents found in the knowledge base.
        </div>
      )}
    </div>
  );
};

